import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import {
  AlertTriangle, CheckCircle, ChevronDown, ChevronRight,
  Wrench, Zap, Shield, Info, FileText, ExternalLink
} from "lucide-react";

const SEVERITY_STYLE = {
  CRITICAL: { bg: "rgba(232,64,87,0.08)", border: "rgba(232,64,87,0.25)", text: "#e84057", icon: AlertTriangle },
  ADVANCED: { bg: "rgba(245,166,35,0.08)", border: "rgba(245,166,35,0.25)", text: "#f5a623", icon: Wrench },
  STD: { bg: "rgba(45,212,168,0.08)", border: "rgba(45,212,168,0.25)", text: "#2dd4a8", icon: Info },
};

export default function ServiceProcedures() {
  const [procedures, setProcedures] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/service/procedures`).then(r => setProcedures(r.data)).catch(() => {});
  }, []);

  if (!procedures) return <div style={{ padding: 40, color: "#6b7280" }}>Loading procedures...</div>;

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1400, margin: "0 auto" }}>
      <style>{`
        .pcard { background:#0e0e16; border:1px solid #1a1a28; border-radius:10px; padding:20px; margin-bottom:16px; }
        .ptitle { font-size:11px; text-transform:uppercase; letter-spacing:2px; color:#6b7280; margin-bottom:14px; display:flex; align-items:center; gap:8px; }
        .step-row { display:flex; gap:12px; padding:12px; border-radius:8px; background:#0a0a12; border:1px solid #1a1a28; margin-bottom:8px; }
        .step-num { width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:12px; flex-shrink:0; }
        .tool-tag { font-size:11px; padding:4px 10px; border-radius:4px; background:rgba(74,158,255,0.08); border:1px solid rgba(74,158,255,0.2); color:#4a9eff; }
        .dtc-tag { font-size:11px; padding:4px 8px; border-radius:4px; background:rgba(232,64,87,0.08); border:1px solid rgba(232,64,87,0.2); color:#e84057; font-family:monospace; }
        .proc-header { display:flex; align-items:center; gap:14px; padding:16px; cursor:pointer; border-radius:10px; border:1px solid #1a1a28; background:#0e0e16; transition:all .15s; margin-bottom:8px; }
        .proc-header:hover { border-color:#2a2a3e; }
      `}</style>

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: "#e2e8f0", letterSpacing: 1, marginBottom: 4 }}>Service Procedures</h1>
        <p style={{ fontSize: 12, color: "#6b7280" }}>
          Detailed step-by-step guides for 2013 Tesla Model S service work
        </p>
      </div>

      {Object.values(procedures).map(proc => {
        const sev = SEVERITY_STYLE[proc.severity] || SEVERITY_STYLE.STD;
        const SevIcon = sev.icon;
        const isExpanded = expandedId === proc.id;

        return (
          <div key={proc.id} data-testid={`procedure-${proc.id}`}>
            {/* Header */}
            <div className="proc-header" onClick={() => setExpandedId(isExpanded ? null : proc.id)} style={{ borderColor: isExpanded ? sev.border : "#1a1a28" }}>
              {isExpanded ? <ChevronDown size={16} color="#6b7280" /> : <ChevronRight size={16} color="#6b7280" />}
              <div style={{ width: 40, height: 40, borderRadius: 10, background: sev.bg, border: `1px solid ${sev.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <SevIcon size={20} color={sev.text} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0" }}>{proc.title}</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{proc.category} / {proc.location}</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: sev.bg, border: `1px solid ${sev.border}`, color: sev.text, letterSpacing: 1 }}>
                {proc.severity}
              </span>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div style={{ marginLeft: 16, marginBottom: 24 }}>
                {/* Description */}
                <div className="pcard">
                  <p style={{ fontSize: 13, color: "#e2e8f0", lineHeight: 1.7 }}>{proc.description}</p>
                  {proc.year_specific && (
                    <p style={{ fontSize: 11, color: "#f5a623", marginTop: 8 }}>
                      <AlertTriangle size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
                      {proc.year_specific}
                    </p>
                  )}
                </div>

                {/* Warnings */}
                {proc.warnings?.length > 0 && (
                  <div className="pcard" style={{ borderColor: "rgba(232,64,87,0.2)" }}>
                    <div className="ptitle"><AlertTriangle size={14} color="#e84057" /> Safety Warnings</div>
                    {proc.warnings.map((w, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0", color: "#e84057", fontSize: 12 }}>
                        <span style={{ color: "#e84057", fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                        {w}
                      </div>
                    ))}
                  </div>
                )}

                {/* Tools */}
                {proc.tools_required?.length > 0 && (
                  <div className="pcard">
                    <div className="ptitle"><Wrench size={14} color="#4a9eff" /> Tools Required</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {proc.tools_required.map((tool, i) => (
                        <span key={i} className="tool-tag">{tool}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Procedure Steps */}
                <div className="pcard">
                  <div className="ptitle"><FileText size={14} color="#2dd4a8" /> Procedure Steps</div>
                  {proc.procedure?.map(step => (
                    <div key={step.step} className="step-row">
                      <div className="step-num" style={{ background: step.warning ? "rgba(245,166,35,0.15)" : "rgba(45,212,168,0.1)", color: step.warning ? "#f5a623" : "#2dd4a8" }}>
                        {step.step}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 13, color: "#e2e8f0" }}>{step.action}</p>
                        {step.warning && (
                          <p style={{ fontSize: 11, color: "#f5a623", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                            <AlertTriangle size={11} /> {step.warning}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* CAN Bus Info (charge port specific) */}
                {proc.can_bus_info && (
                  <div className="pcard">
                    <div className="ptitle"><Zap size={14} color="#f5a623" /> CAN Bus Information</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 4 }}>ECU Name</div>
                        <div style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 600 }}>{proc.can_bus_info.ecu_name}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 4 }}>Bus</div>
                        <div style={{ fontSize: 13, color: "#4a9eff", fontWeight: 600 }}>{proc.can_bus_info.bus}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 4 }}>CAN Messages</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {proc.can_bus_info.can_messages.map((m, i) => (
                          <span key={i} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, background: "rgba(74,158,255,0.08)", border: "1px solid rgba(74,158,255,0.2)", color: "#4a9eff", fontFamily: "monospace" }}>
                            {m}
                          </span>
                        ))}
                      </div>
                    </div>
                    {proc.can_bus_info.wiring_note && (
                      <p style={{ fontSize: 11, color: "#f5a623", marginTop: 8 }}>
                        <Info size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
                        {proc.can_bus_info.wiring_note}
                      </p>
                    )}
                  </div>
                )}

                {/* Related DTCs */}
                {proc.related_dtcs?.length > 0 && (
                  <div className="pcard">
                    <div className="ptitle"><Shield size={14} color="#e84057" /> Related Diagnostic Codes</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {proc.related_dtcs.map((dtc, i) => (
                        <span key={i} className="dtc-tag">{dtc}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Aftermarket OBD2 Note */}
                {proc.aftermarket_obd2_note && (
                  <div style={{ padding: "12px 16px", background: "rgba(245,166,35,0.08)", border: "1px solid rgba(245,166,35,0.25)", borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: "#f5a623", display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>{proc.aftermarket_obd2_note}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
